import { Link } from 'react-router-dom'

import { ExpandIcon, Flex, IconButton, Text } from '@buffer-mono/popcorn'

import { DraftBadge } from '~publish/components/PostStatusBadge'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'

import styles from './ContentItemDetailsCardHeader.module.css'

import type * as React from 'react'
import type { JSX } from 'react'

type ContentItemDetailsCardHeaderProps = {
  contentItemId: string
  targetDate: string | null
}

/**
 * Header for a channel-less draft's preview/details card — the ContentItem
 * counterpart to CalendarPostCardHeader. A draft has no channel, due date, or
 * recurrence, so the header carries only its target date and status, plus the
 * same Expand action into the draft details dialog (relative link: resolves
 * under whichever route currently renders the card).
 */
export function ContentItemDetailsCardHeader({
  contentItemId,
  targetDate,
}: ContentItemDetailsCardHeaderProps): JSX.Element {
  const dateTimeFormatter = useDateTimeFormatter()

  return (
    <ContentItemDetailsCardHeaderView
      dateLabel={
        targetDate
          ? dateTimeFormatter(targetDate, { includeTimeZone: false })
          : 'No date'
      }
      action={
        <IconButton
          as={Link}
          to={`drafts/${contentItemId}`}
          label="Expand"
          size="small"
          variant="tertiary"
        >
          <ExpandIcon />
        </IconButton>
      }
    />
  )
}

export function ContentItemDetailsCardHeaderView({
  dateLabel,
  action,
}: {
  dateLabel: string
  action?: React.ReactNode
}): JSX.Element {
  return (
    <header className={styles.header}>
      <Flex gap="sm" align="center">
        <Text size="md" weight="medium">
          {dateLabel}
        </Text>
        <DraftBadge size="small" />
      </Flex>
      {action}
    </header>
  )
}
